import { useState } from 'react'
import { toast } from 'sonner'
import { Copy, Check, KeyRound } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { copyText } from '../../lib/clipboard'

/**
 * Shows a freshly-minted raw API key EXACTLY ONCE. The caller holds the raw
 * value only while this dialog is open and clears it on close — the key is
 * never persisted in app state afterwards.
 */
export function RevealKeyDialog({ open, rawKey, scopeless = false, onClose }) {
  // Keyed to the key itself, so a NEW minted key resets the label.
  const [copiedValue, setCopiedValue] = useState(null)
  const copied = copiedValue !== null && copiedValue === rawKey

  async function copy() {
    // Routed through the shared helper so a refused/unavailable Clipboard API
    // falls back to a gesture-based execCommand copy, and the label stays on
    // "Copied" rather than reverting (product decision — a confirmation that
    // erases itself left users unsure the copy had happened). See lib/clipboard.js.
    if (!(await copyText(rawKey ?? ''))) {
      toast.error('Could not copy. Please copy it manually.')
      return
    }
    setCopiedValue(rawKey ?? '')
    toast.success('API key copied to clipboard.')
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="API Key Created"
      description="Copy this key now — it is shown only once and cannot be viewed again."
      size="ml"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-[color:var(--color-status-complete)]">
          <KeyRound size={18} />
          <span className="font-medium text-[color:var(--color-ink-strong)]">
            Store this secret securely now.
          </span>
        </div>
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={rawKey ?? ''}
            onFocus={(e) => e.target.select()}
            className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm font-mono text-[color:var(--color-ink)] outline-none"
          />
          <Button variant="secondary" onClick={copy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-xs text-[color:var(--color-priority-urgent)]">
          This key will not be shown again. If it is lost, revoke it and generate a
          new one.
        </p>
        {scopeless ? (
          <p className="text-xs text-[color:var(--color-ink-muted)]">
            This key has no scopes yet — it can reach only the discovery
            endpoints. Use the platform’s Edit action to grant it scopes.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
